import { Inject, Injectable } from '@angular/core';
import { SessionService } from '../session/session.service';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore   is necessary here because it is complaining about importing from json
import translations from './translations.json';
import { DOCUMENT } from '@angular/common';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { LanguageType } from '@monsido_services/services/mon-translate/dist/translate.interface';
import {
    TranslateServiceInjectionToken,
    TranslateServiceInterface,
} from '@monsido/angular-shared-components';
import { TranslocoService } from '@jsverse/transloco';

@Injectable()
export class MonTranslationService {
    private getTranslocoAvailableLangIds (): string[] {
        return this.translocoService.getAvailableLangs().map((lang) => {
            if (typeof lang === 'string') {
                return lang;
            }

            // Transloco allows availableLangs to be configured as objects.
            // We only care about the id.
            return lang.id;
        });
    }

    private normalizeLocaleToSupportedLangId (locale: string): string {
        const normalized = (locale || '')
            .toLowerCase()
            .replace(/_/g, '-');

        const baseLang = normalized.split('-')[0];
        const availableLangs = this.getTranslocoAvailableLangIds();
        const defaultLang = this.translocoService.getDefaultLang() || 'en';

        // If the locale is e.g. "en-US"/"en_US"/"en-us", clamp to "en".
        if (baseLang && availableLangs.includes(baseLang)) {
            return baseLang;
        }

        return defaultLang;
    }

    constructor (
        private translationService: TranslateService,
        @Inject(TranslateServiceInjectionToken)
        private ascTranslationService: TranslateServiceInterface,
        private ng2SessionService: SessionService,
        @Inject(DOCUMENT) private document: Document,
        private translocoService: TranslocoService,
    ) {}

    initializeApp (): Promise<unknown> {
        this.translocoService.setDefaultLang('en');
        this.translocoService.setFallbackLangForMissingTranslation({
            fallbackLang: 'en',
        });
        return new Promise<unknown>((resolve: (value?: unknown) => void) => {
            this.ng2SessionService.meObs.subscribe({
                next: user => {
                    const lang = user?.locale ? user.locale : this.translationService.baseLanguage;
                    this.setLanguage(lang as LanguageType);
                },
            });
            return resolve(null);
        });
    }

    setLanguage (lang: LanguageType): void {
        const supportedLangId = this.normalizeLocaleToSupportedLangId(String(lang));
        const strings = translations[supportedLangId] || translations['en'];

        this.translationService.setCurrentLanguage(supportedLangId as LanguageType);
        this.translationService.setStrings(supportedLangId as LanguageType, strings);
        this.ascTranslationService.setCurrentLanguage(supportedLangId as LanguageType);
        this.ascTranslationService.setStrings(supportedLangId as LanguageType, strings);

        // Keep <html lang> aligned with shipped translation files.
        this.document.documentElement.lang = supportedLangId;
        this.translocoService.setActiveLang(supportedLangId);
    }
}
